import {
    AskQuestionLabel,
    Button, cssX,
    FCX,
    IDropdownItem, includesInLC, markMatch,
    Popup, SearchBoxLoading, SearchBoxNoResults, startsFromInLC,
    ToolSearch as PlatformToolSearch,
    ToolSearchContent,
    ToolSearchHeader,
    ToolSearchInput
} from "@datassential/platform-ui-lib";
import cn from "classnames";
import { useCommonDataContext, useKeywords } from "@core/CommonDataContext";
import { IoClose } from "react-icons/io5";
import { BsStars } from "react-icons/bs";
import { useCallback, useMemo, useState, KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import qs from "query-string";
import ToolSimpleSearchDropdown from "./ToolSimpleSearchDropdown";
import RecentSearches from "@vms/ToolSearch/RecentSearches";
import SuggestedSearches from "@vms/ToolSearch/SuggestedSearches";
import useApplySearch from "@vms/ToolSearch/useApplySearch";

import { getKeywordProfileLink } from "@core/routes";

const ToolSearch: FCX<{
    isOpen: boolean;
    close: () => void;
}> = ({
    isOpen,
    close,
    className,
    testId,
}) => {
    const searchString = qs.parse(window.location.search).SearchString as string ?? '';
    const [search, setSearch] = useState<string>(searchString);

    const { isLoading } = useCommonDataContext();
    const rawKeywords = useKeywords();
    const applySearch = useApplySearch();

    const submitEl = useMemo(() => {
        if (!search.trim().length) return null;

        return (
            <Button className="appears" onClick={() => applySearch(search)}>
                <BsStars/>&nbsp;AI Search
            </Button>
        );
    }, [search, applySearch]);

    const preSortedKeywords = useMemo(
        () => [...rawKeywords]
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
        [rawKeywords]
    );

    const keywords = useMemo(
        () => [
            ...preSortedKeywords.filter(i => startsFromInLC(i.name ?? '', search.trim().toLowerCase())),
            ...preSortedKeywords.filter(i => !startsFromInLC(i.name ?? '', search.trim().toLowerCase()) && includesInLC(i.name ?? '', search.trim().toLowerCase())),
        ].slice(0, 12),
        [preSortedKeywords, search]
    );

    const closeToolSearch = useCallback(() => {
        setSearch("");
        close();
    }, [close]);

    const simpleSearchResults = useMemo(
        (): IDropdownItem[] | undefined => {
            return keywords.length ? keywords.map((item) => ({
                id: item.id,
                content: (
                    <Link
                        to={getKeywordProfileLink(item?.id)}
                        className={cn(cssX("Dropdown__item"), 'js-btn-search')}
                        onClick={closeToolSearch}
                    >
                        <div dangerouslySetInnerHTML={{
                            __html: markMatch(item.name ?? '', search.trim())
                        }}/>
                    </Link>
                ),
            })) : undefined;
        },
        [search, keywords, closeToolSearch]
    );

    const simpleSearchResultChildren = useMemo(() => {
        if (isLoading) return (
            <SearchBoxLoading/>
        );

        if (!simpleSearchResults) return (
            <SearchBoxNoResults/>
        );

        return null;
    }, [isLoading, simpleSearchResults]);

    const hasSearchQuery = useMemo(() => search.trim().length > 0, [search]);

    const onEnterPress = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
        if (hasSearchQuery && event.key === 'Enter') applySearch(search);
    }, [hasSearchQuery, search, applySearch]);

    const hasQuickSearchResults = useMemo(() => {
        return hasSearchQuery && simpleSearchResults?.length;
    }, [hasSearchQuery, simpleSearchResults]);

    return (
        <Popup
            isOpened={isOpen}
            close={closeToolSearch}
            closeOnEsc
            noWrapper
            testId={testId}
        >
            <PlatformToolSearch
                className={cn(
                    "ToolSearch",
                    className
                )}
                testId={"blockToolSearch"}
            >
                <ToolSearchHeader
                    closeEl={
                        <div
                            className="ToolSearch__close"
                            onClick={closeToolSearch}
                        >
                            <IoClose/>
                        </div>
                    }
                    className="appears-bottom"
                >
                    <AskQuestionLabel className="ToolSearch__question-label ToolSearchQuestionLabel">
                        or type in any food, flavor, or beverage to get started.
                    </AskQuestionLabel>
                    <SuggestedSearches/>
                    <ToolSearchInput
                        className="ToolSearch__input ToolSearchInput"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        onKeyUp={onEnterPress}
                        submitEl={submitEl}
                    />
                </ToolSearchHeader>
                <ToolSearchContent className={cn("ToolSearch__content", "appears")}>
                    {hasQuickSearchResults && (
                        <ToolSimpleSearchDropdown
                            className={cn(
                                "ToolSearch__simple-result",
                                "appears-top",
                                "fast"
                            )}
                            items={simpleSearchResults}
                            testId="blockToolSearchSimpleResult"
                            isVisible
                        >
                            {simpleSearchResultChildren}
                        </ToolSimpleSearchDropdown>
                    )}
                    <RecentSearches className={cn(hasQuickSearchResults && "is-hidden")}/>
                </ToolSearchContent>
            </PlatformToolSearch>
        </Popup>
    );
};

export default ToolSearch;